'use client';

import { useMemo, useState } from 'react';
import {
  getSortableDisplayOptions,
  resolveEncountersDisplayOptions,
  resolveEncountersMetricCode,
} from 'src/utils/strategyHelpers';
import type { StrategyMetric } from 'src/api/redux/strategy/types';
import type { MetricDefinition } from 'src/api/redux/strategy/allStrategiesApi';

export type StrategySortOrder = 'asc' | 'desc';

/** The sort a Strategy Center results view is on. `sort: 'metric'` sorts by `sort_metric`. */
export interface StrategySortValues {
  sort: string;
  order: StrategySortOrder;
  sort_metric: string | null;
}

/** The sort query params for the assigned-strategies members/map endpoints. */
export interface StrategySortParams {
  sort?: string;
  order?: StrategySortOrder;
  sort_metric?: string | null;
}

/** Before a metric default is known: a metric sort with no code, which is not ready. */
export const PENDING_METRIC_SORT: StrategySortValues = {
  sort: 'metric',
  order: 'desc',
  sort_metric: null,
};

/** Multi-chain default: metrics don't align across strategies, so sort alphabetically. */
export const NAME_SORT: StrategySortValues = {
  sort: 'name',
  order: 'asc',
  sort_metric: null,
};

interface UseStrategyMetricSortParams {
  selectedChainIds: number[];
  /** Per-strategy metrics (`results` of `useGetStrategyMetricsQuery`) for the one selected chain. */
  strategyMetrics?: StrategyMetric[];
  /**
   * True while the selected chain or its metrics are still loading, so no default is
   * applied (and `fallbackSort` is not used) until they resolve.
   */
  metricsLoading?: boolean;
  /** `metric_definitions` from the members response; used when the per-strategy metrics have no display option. */
  metricDefinitions?: Record<string, MetricDefinition>;
  /** Single-chain default once metrics resolve with no display option at all. */
  fallbackSort?: StrategySortValues;
  showEncountersTotals: boolean;
  /**
   * Whether the default must be an option the sort dropdown offers (the default, for
   * the map list). The table has no dropdown and can only sort by the option its column
   * displays, so it passes `false` to default to that first displayed option instead.
   */
  defaultToSortableOption?: boolean;
}

const defaultSortKey = (
  chainKey: string,
  defaultSort: StrategySortValues | null
): string =>
  defaultSort
    ? `${chainKey}|${defaultSort.sort}|${defaultSort.order}|${defaultSort.sort_metric ?? ''}`
    : `${chainKey}|`;

/**
 * Sort state shared by the Strategy Center list and map results.
 *
 * - One chain: defaults to the first metric's first display option, descending, as soon
 *   as that option is known (falling back to the members response `metric_definitions`,
 *   then to `fallbackSort`). Re-applies the default whenever the chain selection or the
 *   default option changes.
 * - Several chains: defaults to name ascending (metrics don't align across strategies).
 * - `sortReady` is false until a metric default is known; `sortParams` is `{}` then, so
 *   callers either omit the sort or skip the request.
 * - Behind the `show-encounters-totals` flag, a `*_claims` metric sort requests its
 *   `*_encounters` sibling (same swap as the displayed value and heatmap), so
 *   `sortValues.sort_metric` keeps the claims code as its identity while
 *   `sortParams.sort_metric` carries the code to request.
 *
 * @example
 * const { sortValues, setSortValues, sortReady, sortParams } = useStrategyMetricSort({
 *   selectedChainIds,
 *   strategyMetrics: strategyMetrics?.results,
 *   showEncountersTotals: !!showEncountersTotals,
 * });
 * const queryParams = useMemo(() => ({ chain_id: selectedChainIds, ...sortParams }), [...]);
 */
const useStrategyMetricSort = ({
  selectedChainIds,
  strategyMetrics,
  metricsLoading = false,
  metricDefinitions,
  fallbackSort,
  showEncountersTotals,
  defaultToSortableOption = true,
}: UseStrategyMetricSortParams) => {
  const chainKey = selectedChainIds.join(',');

  // By default the first option the sort dropdown would offer (same filtering), so the
  // default is always one of its choices; otherwise the first option the table shows.
  const firstMetricDisplayOptionCode = useMemo(() => {
    const firstSortableCode = (
      metrics: Array<{
        label?: string;
        display_options?: Array<{ code: string; label: string; type?: string }>;
      }>
    ) =>
      metrics
        .filter((metric) => metric?.label)
        .flatMap((metric) =>
          defaultToSortableOption
            ? getSortableDisplayOptions(metric.display_options, showEncountersTotals)
            : resolveEncountersDisplayOptions(
                metric.display_options,
                showEncountersTotals
              )
        )[0]?.code;

    return (
      firstSortableCode(strategyMetrics ?? []) ??
      firstSortableCode(Object.values(metricDefinitions ?? {})) ??
      null
    );
  }, [
    strategyMetrics,
    metricDefinitions,
    showEncountersTotals,
    defaultToSortableOption,
  ]);

  const defaultSort = useMemo<StrategySortValues | null>(() => {
    if (selectedChainIds.length > 1) return NAME_SORT;
    if (selectedChainIds.length === 0) return null;
    if (firstMetricDisplayOptionCode) {
      return {
        sort: 'metric',
        order: 'desc',
        sort_metric: firstMetricDisplayOptionCode,
      };
    }
    if (metricsLoading) return null;
    return fallbackSort ?? null;
  }, [
    selectedChainIds.length,
    firstMetricDisplayOptionCode,
    metricsLoading,
    fallbackSort,
  ]);

  // The default is applied during render (not in an effect) so the request that follows
  // a chain or metric change never goes out with the previous chain's sort.
  const currentDefaultKey = defaultSortKey(chainKey, defaultSort);
  const [appliedDefaultKey, setAppliedDefaultKey] = useState(currentDefaultKey);
  const [sortValues, setSortValues] = useState<StrategySortValues>(
    () => defaultSort ?? PENDING_METRIC_SORT
  );
  if (appliedDefaultKey !== currentDefaultKey) {
    setAppliedDefaultKey(currentDefaultKey);
    setSortValues(defaultSort ?? PENDING_METRIC_SORT);
  }

  const sortReady = sortValues.sort !== 'metric' || !!sortValues.sort_metric;

  // All metric codes (incl. the encounters siblings) drive the sort swap below.
  const allMetricCodes = useMemo(() => {
    const codes = new Set<string>();
    (strategyMetrics ?? []).forEach((metric) =>
      metric?.display_options?.forEach(
        (option) => option?.code && codes.add(option.code)
      )
    );
    return codes;
  }, [strategyMetrics]);

  const effectiveSortMetric =
    showEncountersTotals &&
    sortValues.sort === 'metric' &&
    sortValues.sort_metric
      ? resolveEncountersMetricCode(sortValues.sort_metric, allMetricCodes, true)
      : sortValues.sort_metric;

  const sortParams = useMemo<StrategySortParams>(
    () =>
      sortReady
        ? {
            sort: sortValues.sort,
            order: sortValues.order,
            sort_metric: effectiveSortMetric,
          }
        : {},
    [sortReady, sortValues.sort, sortValues.order, effectiveSortMetric]
  );

  return useMemo(
    () => ({
      sortValues,
      setSortValues,
      sortReady,
      effectiveSortMetric,
      sortParams,
    }),
    [sortValues, sortReady, effectiveSortMetric, sortParams]
  );
};

export default useStrategyMetricSort;
